import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEY = "cocoon_access_token";
const REFRESH_KEY = "cocoon_refresh_token";

export const authStore = {
  get: () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } },
  getRefresh: () => { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } },
  set: (access, refresh) => {
    try {
      if (access) localStorage.setItem(TOKEN_KEY, access);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    } catch {}
  },
  clear: () => { try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(REFRESH_KEY); } catch {} },
};

// Active family profile (Compte Famille). Stored per session so a fresh login re-prompts "Qui explore ?".
const PROFILE_KEY = "cocoon_active_profile_id";
export const profileStore = {
  get: () => { try { return sessionStorage.getItem(PROFILE_KEY); } catch { return null; } },
  set: (id) => { try { if (id) sessionStorage.setItem(PROFILE_KEY, id); else sessionStorage.removeItem(PROFILE_KEY); } catch {} },
  clear: () => { try { sessionStorage.removeItem(PROFILE_KEY); } catch {} },
};

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Attach the Bearer token when available (needed when third-party cookies are blocked).
api.interceptors.request.use((config) => {
  const token = authStore.get();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const pid = profileStore.get();
  if (pid) {
    config.headers = config.headers || {};
    config.headers["x-profile-id"] = pid;
  }
  return config;
});

// On a 401, try a one-time silent refresh using the stored refresh token, then retry.
let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh");
    if (status === 401 && !original._retried && !isAuthCall) {
      const refreshTok = authStore.getRefresh();
      if (refreshTok) {
        try {
          original._retried = true;
          if (!refreshing) {
            refreshing = axios.post(`${BACKEND_URL}/api/auth/refresh`, { refresh_token: refreshTok }, { withCredentials: true })
              .finally(() => { refreshing = null; });
          }
          const { data } = await refreshing;
          if (data?.access_token) authStore.set(data.access_token, null);
          original.headers = original.headers || {};
          const newTok = authStore.get();
          if (newTok) original.headers.Authorization = `Bearer ${newTok}`;
          return api(original);
        } catch (e) {
          authStore.clear();
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessaie.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
